const standards = [
  {
    number: '01',
    title: 'We Scope It Honestly',
    description:
      'Before any work begins, we assess what is actually needed — not what generates more billable hours. You get a detailed, honest quote with no fluff.',
    accent: 'Transparent Pricing',
  },
  {
    number: '02',
    title: 'We Communicate Every Step',
    description:
      'You know our timeline. You know when we arrive. You know when the inspection is scheduled. No radio silence, no guesswork.',
    accent: 'Proactive Updates',
  },
  {
    number: '03',
    title: 'We Work Clean',
    description:
      'Every job site is left cleaner than we found it. Protective coverings go down before any work starts. Debris is removed before we leave.',
    accent: 'Job-Site Respect',
  },
  {
    number: '04',
    title: 'We Pull Permits & Pass Inspections',
    description:
      'All permitted work is properly documented. We coordinate with local inspectors and make sure every installation is code-compliant before sign-off.',
    accent: 'Code Compliance',
  },
  {
    number: '05',
    title: 'We Stand Behind Our Work',
    description:
      'If something isn\'t right, we make it right. Our warranty on workmanship isn\'t fine print — it\'s our commitment to every client we serve.',
    accent: 'Backed by Warranty',
  },
];

const StandardsSection = () => (
  <section className="relative bg-dp py-28 overflow-hidden">
    {/* Glow accents */}
    <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[var(--bd)] to-transparent" />
    <div className="absolute top-24 right-0 w-[500px] h-[500px] bg-[#00D9FF]/[0.02] rounded-full blur-3xl pointer-events-none" />

    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">

        {/* Left: standards list */}
        <div className="scroll-reveal scroll-from-left">
          <span className="inline-block text-[#00D9FF] text-[11px] font-bold tracking-[3px] uppercase mb-5">
            How We Operate
          </span>
          <h2 className="text-4xl md:text-5xl font-black text-[var(--tp)] leading-tight tracking-tight mb-14">
            Our Standards
            <br />
            <span className="text-[var(--tm)]">of Service.</span>
          </h2>

          {/* Standards list */}
          <div className="flex flex-col">
            {standards.map((s, idx) => (
              <div
                key={s.number}
                className="group relative flex gap-6 pb-10 last:pb-0"
              >
                {idx < standards.length - 1 && (
                  <div className="absolute left-5 top-10 bottom-0 w-[1px] bg-gradient-to-b from-[var(--bd)] to-transparent" />
                )}
                <div className="flex-shrink-0 w-10 h-10 rounded-full border border-[var(--bd)] group-hover:border-[#00D9FF]/40 bg-pg flex items-center justify-center transition-all duration-300 mt-0.5">
                  <span className="text-[#00D9FF] text-[11px] font-black">{s.number}</span>
                </div>
                <div className="flex-1 pt-1.5">
                  <span className="inline-block text-[#00D9FF]/80 text-[10px] font-bold tracking-[2px] uppercase mb-2 border border-[#00D9FF]/20 px-2.5 py-1 rounded-full group-hover:border-[#00D9FF]/40 transition-colors duration-300">
                    {s.accent}
                  </span>
                  <h3 className="text-[var(--tp)] font-bold text-[18px] mb-2 leading-snug group-hover:text-[#00D9FF]/90 transition-colors duration-300">
                    {s.title}
                  </h3>
                  <p className="text-[var(--tm)] text-[14px] leading-relaxed">{s.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: image block */}
        <div className="lg:sticky lg:top-32 flex flex-col gap-6 scroll-reveal scroll-from-right">
          <div className="relative rounded-2xl overflow-hidden">
            <img
              src="https://static.readdy.ai/image/577c45d6defa6ac02f4bf39c73856fa0/b161391bfb0ae0a055a259172c9ffdca.png"
              alt="DOC Electric planning electrical work"
              className="w-full object-cover object-top"
              style={{ height: '320px' }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#060E1C]/60 to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 flex items-center gap-3">
              <div className="w-2 h-2 rounded-full bg-[#00D9FF] animate-pulse flex-shrink-0" />
              <span className="text-white/80 text-[12px] font-medium">Pre-job scope review — every single time</span>
            </div>
          </div>

          <div className="relative rounded-2xl overflow-hidden">
            <img
              src="https://public.readdy.ai/ai/img_res/cb01285b4e1c5a9c050571e90175561f.jpg"
              alt="DOC Electric clean panel work"
              className="w-full object-cover object-top"
              style={{ height: '240px' }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#060E1C]/50 to-transparent" />
            <div className="absolute top-4 right-4 flex items-center gap-2 bg-[#0A1628]/90 border border-[#00D9FF]/20 rounded-full px-3 py-1.5 backdrop-blur-sm">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-checkbox-circle-fill text-[#00D9FF] text-sm" />
              </span>
              <span className="text-white text-[11px] font-bold whitespace-nowrap">Inspection Passed</span>
            </div>
          </div>

          <div className="bg-pg border border-[var(--bd)] rounded-xl p-5 flex items-start gap-4">
            <span className="w-10 h-10 flex items-center justify-center bg-[#00D9FF]/10 rounded-lg flex-shrink-0">
              <i className="ri-map-pin-2-fill text-[#00D9FF] text-lg" />
            </span>
            <div>
              <div className="text-[var(--tp)] font-bold text-[14px] mb-0.5">Proudly Local to Kalamazoo</div>
              <div className="text-[var(--tm)] text-[13px] leading-relaxed">
                Based in Kalamazoo, serving Portage, Battle Creek, Paw Paw, and all of southwest Michigan.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default StandardsSection;
